import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { route } from '~/app/helpers/route';
import usePageTitle from '~/app/hooks/usePageTitle';
import useTranslation from '~/app/hooks/useTranslation';
import { useAppHeader } from '~/app/modules/appHeader';
import { useStoreActions, useStoreState } from '~/store';
import { Machine, SearchMachine, SearchUser } from '~/types';
import MForm from '~/app/modules/form';
import classes from './style.module.scss';
import UserMultiSelect from '~/app/modules/form/UserMultiSelect/index';
import Text from '~/app/modules/form/Text';
import Date from '~/app/modules/form/Date';
import DynamicChecklist from './Checklist';
import Textarea from '~/app/modules/form/Textarea';
import Button from '~/app/common/Button';
import External from './External';
import { AttachmentsValue, ChecklistItem, ExternalResource, TrainingFormType } from '../types';
import useForm, { UseFormOptionFields } from '~/app/modules/form/useForm';
import moment from 'moment';
import { required, requiredArray, requiredBoolean } from '~/app/helpers/validation';
import Loader from '~/app/common/Loader';
import { ROUTES } from '~/app/Router';
import { toast } from 'react-toastify';
import useDidMountEffect from '~/app/hooks/useDidMountEffect';
import ReactTooltip from 'react-tooltip';
import Icon from '~/app/common/Icon';
import Attachments from './Attachments';
import MachineMultiSelect from './MachineMultiSelect';

export type TrainingFormComponent = React.FC;

const TrainingForm: TrainingFormComponent = () => {

  const { t } = useTranslation("training");
  const { t: t2 } = useTranslation('machine.details');
  const { id } = useParams<{ id: Machine['id'] }>();

  const history = useHistory();
  const { setData } = useAppHeader();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);

  const initialized = useRef<boolean>(false);
  const user = useStoreState(state => state.user.data);
  const machineId = useStoreState(state => state.machine.data.id);
  const machineName = useStoreState(state => state.machine.data.name)
  const intern = useStoreState(state => state.machine.data.intern)
  const checklist = useStoreState(state => state.training.checklist);
  const fetchChecklistAction = useStoreActions(actions => actions.training.fetchChecklist);
  const saveTrainingAction = useStoreActions(actions => actions.training.saveTraining);
  const [attachments, setAttachments] = useState<AttachmentsValue>({ images: [], pdfs: [] });

  const formFields = useMemo(() => {
    const fields: UseFormOptionFields = [
      ['excavators', { validators: [['required', requiredArray]] }],
      ['title', { validators: [['required', required]] }],
      ['participants', { validators: [['required', requiredArray]] }],
      'instructor',
      ['trainingDate', { validators: [['required', required]] }],
      ['checklist', { validators: [['required', requiredArray]] }],
      'externalResources',
      'commentFromInstructor',
      ['signConfirm', { validators: [['required', requiredBoolean]] }]
    ];

    return fields;
  }, []);

  const { formState, values, errors, setValue, triggerValidation, fieldEvents, formEvents, removeForm } = useForm<TrainingFormType>({
    formName: 'trainingForm',
    fields: formFields,
    defaultValues: {
      excavators: [],
      title: '',
      participants: [],
      instructor: user.id,
      trainingDate: moment().format('YYYY-MM-DD'),
      checklist: [],
      externalResources: [],
      commentFromInstructor: '',
      signConfirm: false
    },
    validationTrigger: 'onBlur'
  });

  const valuesRef = useRef(values);
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  const updateChecklistItems = useCallback((machineIds: string[]) => {
    const seenItems: ChecklistItem[] = [];
    const checklistMap = new Map<string, ChecklistItem>();
    const currentChecklist = valuesRef?.current?.checklist as ChecklistItem[] ?? [];
    currentChecklist.forEach(item => {
      const title = item.title.toLowerCase().trim();
      checklistMap.set(title, item);
    });
    for (let index = 0; index < machineIds.length; index++) {
      const machineId = machineIds[index];
      const checklistItems = checklist[machineId];
      if (checklistItems) {
        checklistItems.forEach(item => {
          const title = item.title.toLowerCase().trim();
          const seenItem = seenItems.find(seenItem => title === seenItem.title.toLowerCase().trim());
          const existingItem = checklistMap.get(title);
          if (!seenItem) {
            if (existingItem) {
              seenItems.push(existingItem);
            } else {
              seenItems.push(item);
            }
          }
        });
      }
    }
    setChecklistItems(seenItems);
    setValue('checklist', seenItems);
  }, [checklist, setValue]);  

  const handleMachinesChange = useCallback((selected: SearchMachine[]) => {
    const current = valuesRef.current?.excavators;
    if (initialized.current && current && JSON.stringify(current) !== JSON.stringify(selected)) {
      setValue('excavators', selected);
      updateChecklistItems(selected.map(item => item.id));
      if (!selected.length) triggerValidation('excavators', selected);
    }
  }, [setValue, triggerValidation, updateChecklistItems]);

  const handleOptionChange = useCallback((selected: SearchUser[]) => {
    const current = valuesRef.current?.participants;
    if (initialized.current && current && JSON.stringify(current) !== JSON.stringify(selected)) {
      setValue('participants', selected);
      if (!selected.length) triggerValidation('participants', selected);
    }
  }, [setValue, triggerValidation]);

  usePageTitle(t2('registerTraining'));

  useEffect(() => {
    setData({
      title: t2('registerTraining'),
      subTitle: `${intern} - ${machineName}`.toUpperCase(),
      backLink: history.length > 1 ? history.goBack : route(ROUTES.projects),
      showUser: false,
      machineId: '',
      projectId: '',
      showMachineMessages: false,
      isIssueDetailPage: false,
      notification: false,
      search: false
    });
  }, [setData, id, intern, machineName, t, t2, history]);

  const handleSubmit = (data) => {
    if (data.formState.invalid) {
      toast.error(t('requiredAll'));
      return;
    }
    setIsLoading(true);
    data.values.participants = data.values.participants
      .filter((p: any) => String(p?.id ?? p) !== String(user?.id))
      .map((p: any) => p?.id ?? p);
    data.values.excavators = data.values.excavators.map(m => m.id);
    saveTrainingAction({
      ...data.values,
      images: attachments.images,
      pdfs: attachments.pdfs
    }).then(res => {
      setIsLoading(false);
      if (res.success) {
        toast.info(
          <div>
            <strong>{t('trainingRegisteredMsg')}</strong><br />
            <small style={{ marginTop: 4 }}>{t('trainingApprovalMsg')}</small>
          </div>,
          { position: 'top-center', className: classes['custom-reg-toast'], autoClose: 2000, closeButton: true }
        );
        history.goBack();
      }
    });
  };

  useDidMountEffect(() => {
    return () => {
      removeForm();
    }
  });

  useEffect(() => {
    if (!initialized.current && machineId) {
      setValue('excavators', [{ id: machineId, name: machineName, intern, isDeleteRestricted: true } as SearchMachine]);
      updateChecklistItems([machineId]);
      initialized.current = true;
    }
  }, [machineId, machineName, intern, setValue, updateChecklistItems]);

  useEffect(() => {
    fetchChecklistAction();
  }, [fetchChecklistAction]);
  
  if (isLoading || !initialized.current || !values) {
    return <Loader size="32"/>;
  }

  return (
    <div className={classes['container']}>
      <div className={classes['header']}>{t2('registerTraining')}</div>
      <MForm onSubmit={formEvents.onSubmit(handleSubmit)}>

        {/* machines */}
        <div className={classes['formLabel']}>
          {t('machine')} <span className={classes['required']}>*</span>
        </div>
        <div className={classes['formField']}>
          <MachineMultiSelect
            defaultOptions={values?.excavators as SearchMachine[] ?? []}
            optionChange={handleMachinesChange}
            position='bottom'
          />
        </div>
        {errors.excavators && (<div className={classes['error-message']}>{t('required')}</div>)}

        {/* participants */}
        <div className={`${classes['formLabel']} ${classes['participant-label']}`}>
          {t('participants')} <span className={classes['required']}>*</span>
          <div className={classes['info-icon']}>
            <Icon name='info' size={14} data-tip={t('participantsTooltip')} />
            <ReactTooltip place='top' effect="solid" />
          </div>
        </div>
        <div className={classes['formField']}>
          <UserMultiSelect
            defaultOptions={values?.participants as SearchUser[] ?? []}
            optionChange={handleOptionChange}
            excludeUserId={Number(user.id)}
            position='bottom'
            placeholder={t('selectParticipants') + '...'}
          />
        </div>
        {errors.participants && (<div className={classes['error-message']}>{t('required')}</div>)}

        {/* title */}
        <div className={`${classes['formLabel']} ${classes['required']}`}>{t('title')}</div>
        <div className={classes['formField']}>
          <Text placeholder={t('enterTitle')} name='title'
            value={values.title as string}
            errors={errors.title}
            onBlur={fieldEvents.onBlur}
            onChange={e => setValue('title', e.target.value)}
          />
        </div>

        {/* instructor name */}
        <div className={classes['formLabel']}>{t('instructorName')}</div>
        <div className={classes['formField']}>
          <Text placeholder={t('enterInstructorName')} value={user.name} disabled/>
        </div>

        {/* register date */}
        <div className={`${classes['formLabel']} ${classes['required']}`}>{t('date')}</div>
        <div className={classes['formField']}>
          <Date
            type='date' name="trainingDate"
            placeholder={'DD.MM.YYYY'} format={'DD.MM.YYYY'}
            min={moment().format('YYYY-MM-DD')}
            defaultValue={values?.trainingDate as string}
            onBlur={fieldEvents.onBlur}
            onChange={e => setValue('trainingDate', e.target.value)}
          />
        </div>
        {errors.trainingDate && (<div className={classes['error-message']}>{t('required')}</div>)}

        {/* checklist */}
        <div className={`${classes['formLabel']} ${classes['required']}`}>{t('checklist')}</div>
        {(checklistItems ?? []).length === 0 && (<div className={classes['info-message']}>{t('noChecklistFound')}</div>)}
        <div className={classes['formField']}>
          <DynamicChecklist
            initialItems={checklistItems}
            onChecklistChange={(checklist: ChecklistItem[]) => setValue('checklist', checklist)}
          />
        </div>
        {errors.checklist && (<div className={classes['error-message']}>{t('required')}</div>)}

        {/* comment */}
        <div className={classes['formLabel']}>{t('commentFromInstructor')}</div>
        <div className={classes['formField']}>
          <Textarea placeholder={t('addComments') + '...'}
            value={values?.commentFromInstructor as string}
            onChange={e => setValue('commentFromInstructor', e.target.value)}
          />
        </div>

        {/* External Resources */}
        <div className={classes['formLabel']}></div>
        <div className={classes['formField']}>
          <External
            initialItems={values?.externalResources as ExternalResource[] ?? []}
            onExternalResourceChange={(externalResources: ExternalResource[]) => setValue('externalResources', externalResources)}
          />
        </div>

        {/* Attachments */}
        <div className={classes['formLabel']}></div>
        <div className={classes['formField']}>
          <Attachments onChange={setAttachments} />
        </div>

        {/* Sign */}
        <div className={`${classes['formLabel']} ${classes['sign-section']}`}>
          <div className={classes['sign-checkbox']}>
            <input className={classes["checkbox"]} type="checkbox" checked={values?.signConfirm as boolean ?? false} onChange={e => setValue('signConfirm', e.target.checked)} />
            <div>{t('signConfirm')}</div>
          </div>
          {errors.signConfirm && (<div className={classes['error-message']}>{t('required')}</div>)}
          <div className={classes['submission-info']}>
            <span>📋 {t('submitInfo')}.</span>
          </div>
        </div>

        <Button className={classes['submit-button']} disabled={formState.invalid} type="submit">{t('submitButton')}</Button>
      </MForm>
    </div>
  );
};

export default TrainingForm
